import { AlertTriangle, Loader2, Pencil, X } from 'lucide-react';
import type { SyntheticEvent } from 'react';
import { cn } from '@/lib/infra/utils';

interface OrderActionButtonsProps {
  disabled?: boolean;
  canceling?: boolean;
  onEdit: () => void;
  onCancel: () => void | Promise<void>;
  className?: string;
  /** Hide edit when only cancellation is proven safe. */
  editAvailable?: boolean;
  /** #748 — terminal, unconfirmed and retryable. The control stays LIVE (that
   *  is the point: the trader gets it back), but must not look untouched, or
   *  "never clicked" and "asked, and we cannot confirm" are indistinguishable.
   *  Ignored while `canceling`, which is the stronger state. */
  unconfirmed?: boolean;
  /** #748 — overrides the cancel control's title/aria when the caller holds a
   *  more specific sentence than this component can derive (the Polymarket
   *  fence's timeout message). */
  cancelTitle?: string;
}

export function OrderActionButtons({
  disabled = false,
  canceling = false,
  onEdit,
  onCancel,
  className,
  editAvailable = true,
  unconfirmed = false,
  cancelTitle,
}: OrderActionButtonsProps) {
  const stop = (e: SyntheticEvent) => {
    e.stopPropagation();
  };

  // #241: `canceling` outlives the DELETE — it stays true until an authoritative
  // Open Orders snapshot drops the ID. Keeping the "Cancel order" name through
  // that window reads as actionable on a control that is deliberately inert.
  const cancelLabel =
    cancelTitle ??
    (canceling ? 'Cancel pending — waiting for Open Orders' : 'Cancel order');

  return (
    <div
      className={cn('inline-flex items-center gap-1', className)}
      onClick={stop}
      onPointerDown={stop}
    >
      {editAvailable && <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        disabled={disabled || canceling}
        title="Edit price"
        aria-label="Edit price"
        className="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex h-6 w-6 items-center justify-center rounded transition-colors disabled:opacity-50"
      >
        <Pencil size={13} />
      </button>}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void onCancel();
        }}
        disabled={disabled || canceling}
        title={cancelLabel}
        aria-label={cancelLabel}
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded transition-colors disabled:opacity-50',
          // Amber is the house could-not-confirm tone (the #241 timeout notices
          // use it); red stays the ordinary destructive-hover affordance.
          !canceling && unconfirmed
            ? 'text-amber-400 hover:bg-amber-500/10 hover:text-amber-300'
            : 'text-muted-foreground hover:bg-red-500/10 hover:text-red-400',
        )}
      >
        {canceling ? (
          <Loader2 size={13} className="animate-spin" />
        ) : unconfirmed ? (
          <AlertTriangle size={13} />
        ) : (
          <X size={14} />
        )}
      </button>
    </div>
  );
}

export default OrderActionButtons;
